import type { ModuleSnapshot } from '@ctcells/contract'
import { enumLabel, t } from '@ctcells/i18n'
import type { ReactNode } from 'react'

import { StatusBadge } from '../../atoms/StatusBadge.tsx'
import { SidePanelSection } from '../../layout/SidePanel.tsx'
import { staleValue } from '../format.ts'

import {
  poseDisplayOrder,
  poseLabel,
  poseRobotCount,
  poseValue,
  robotPose,
} from './robotPoseModel.ts'
import { gripText, robotGrips } from './robotTransferModel.ts'

/**
 * 6축 상세 칸 — 「운송 로봇」 카드(끝장치 · 상태 칩 · 위치 · 그리퍼). 장비별 보기에서 장비 칩 「운송 로봇」을 고르면 오른쪽 위 메트릭 상자 맨 위(`lead`)에 선다.
 * 🔴 «핸드오프 자리» 표(장비 × 거치대 · 슬라이드 찼음/빔)는 걷었다(eric 2026-10-21 「이게.. 지금 운송장비 그림상에 표현되고 있으면.. 이거 지워줘」) —
 *    같은 서버 값(`handoff_occupancy`)을 운송 로봇 그림(`RobotLine`)이 정거장마다 트레이 판(찼음 · 이름 모르면 글자 없는 판) · 점선 자리(빔) ·
 *    사유 글자(모듈 없음 · 미발행 · 발행한 자리 0개)로 말한다.
 * 알람 수 · 제어 소유 · 응답 확인 · 보류 · 낡음 표지는 그 카드의 «정보» 구역과 머리가 종전대로 말한다 — 여기서 다시 적지 않는다.
 *
 * 🔴 **값은 응답 그대로**다(1Hz `module_status` 미러). 이동 구간 · 집은 트레이는 응답에 없어 타일을 만들지 않는다(BR-114).
 * 🔴 **«위치» 구역**(v0.9.4 · 2026-10-11) — 목록 `GET /modules` 의 `pose_telemetry`(TCP 좌표 · 자세 · 상하축 · 좌우축)를 숫자 그대로
 *    (단위는 키 접미). 모의 값(`pose_simulated: 1`)이면 「모사값」 표지. 받은 봉투가 없거나(`null`) 이 응답에 칸이 없으면 **구역째 없다** —
 *    eric 이 「위치 미발행」류 글자를 두 번 걷었다(2026-10-08 「이거 빼줘」 · 「위치 미발행 텍스트 이런 거 빼」). 「0」으로 접어 세우지도 않는다.
 *    해석은 `robotPoseModel.ts`. 인터록이 미발행(`null`)이면 「닫힘」으로 접지 않고 타일을 안 만든다(사전 머리말과 같은 규칙).
 * 🔴 구독이 끊겼거나 6축이 낡았으면 상태 · 그리퍼 · 실행은 «마지막 값»이다.
 */
export function RobotDetailTiles({
  snapshot,
  stale = false,
}: {
  snapshot: ModuleSnapshot | undefined
  stale?: boolean | undefined
}) {
  if (snapshot === undefined) return null
  const old = stale || snapshot.stale === true
  /* 상태는 칸이 아니라 「6축 로봇」 카드 제목 오른쪽 칩이다(eric 2026-10-12 「6축 로봇 오른쪽에.. 대기 칩을 해줘」) */
  const stateChip = (
    <StatusBadge
      label={staleValue(enumLabel('enum.moduleState', snapshot.state), old)}
      status={snapshot.state}
    />
  )
  /*
   * 그리퍼 — 「위치」 카드의 마지막(9번째) 칸이다(eric 2026-10-12 「그리퍼 rob6.1 해제 이거를.. 위치 맨 마지막에.. 9번째에 넣어줘. 3축 로봇과 타입틀과
   * 밸류 값 유사하게」). 로봇이 하나면 3축 로봇 칸처럼 상태 이름만(「해제」), 여럿이면 어느 그리퍼인지 앞에 붙인다.
   * 「실행」 · 「인터록」 · 「복구 시도」 칸은 걷었다(같은 날 「실행 없음.. 이 건 빼고」 · 「3축 로봇 처럼 못 없애?」) — 인터록이 열리거나 복구가 걸리면
   * 알람 · 작업 줄 멈춤 한 줄이 말한다
   */
  const grips = robotGrips(snapshot)
  const gripTile = {
    key: 'grip',
    label: t('monitoring.robot.gripper'),
    value: staleValue(
      grips !== undefined && grips.length === 1 && grips[0] !== undefined
        ? enumLabel('enum.gripState', grips[0].state)
        : gripText(grips),
      old,
    ),
  }
  return (
    <>
      {/*
        🔴 맨 위 「운송 로봇」 카드 = 제목 오른쪽 상태 칩 + 칸 아홉(TCP · 부가축 · 그리퍼). 끝장치 줄(「엔드 이펙터: 그리퍼 (PGEA-50-40)」)은 걷었다
           (eric 2026-10-21 「엔드 이펙터: 그리퍼 (PGEA-50-40) 이거 지워줘 운송 로봇쪽에 말이야..」)
           (eric 2026-10-12 「동작이 실제로 6축 로봇 값이고.. 6축 로봇 안에 동작을 먼저 넣고.. 그 아래에 위치값 카드를 하나 더 넣어줘」)
      */}
      <SidePanelSection badge={stateChip} title={t('monitoring.robot.card')}>
        {/* 위치(TCP · 부가축) · 그리퍼 칸 — 6축 로봇 카드 안에 바로(같은 날 「6축 로봇 안에.. 위치 카드」 → 「위치 텍스트랑 둘러싸는 카드 없애」) */}
        <RobotPosition grip={gripTile} old={old} snapshot={snapshot} />
      </SidePanelSection>
    </>
  )
}

/**
 * 6축 «위치» — TCP 좌표 · 자세 · 부가축을 응답 그대로. 🔴 낡았으면 «마지막 값» · 모사값이면 표지(글자로 — 색만으로 가르지 않는다).
 * 🔴 `null` · 키 부재는 모델이 가르지만(`robotPose`) 화면은 둘 다 «안 그린다».
 */
function RobotPosition({
  snapshot,
  old,
  grip,
}: {
  snapshot: ModuleSnapshot
  old: boolean
  /** 마지막 칸 — 그리퍼(위치를 못 받아도 이 칸은 선다) */
  grip: { key: string; label: string; value: ReactNode }
}) {
  const pose = robotPose(snapshot)
  // 🔴 받은 위치가 없거나(`null`) 칸이 없으면 위치 칸은 «안 그린다» — 사유 글자도 세우지 않는다(머리말 · eric 2026-10-08). 그리퍼 칸만 남는다
  const readings = pose.kind === 'read' ? poseDisplayOrder(pose.readings) : []
  const robots = poseRobotCount(readings)
  /* 「위치」 제목과 둘러싼 안쪽 카드는 걷었다 — 칸만 6축 로봇 카드 안에 바로 선다(eric 2026-10-12 「위치 텍스트랑.. 둘러싸는 카드 없애달라고」) */
  /* 「모사값 — 모듈이 실측이 아닌 값이라고 표시했습니다」 줄은 걷었다(eric 2026-10-12 「이거 제거하고」) */
  return (
    <dl
      className="eqtiles eqtiles--3"
      data-robot-detail=""
      data-robot-pose={pose.kind === 'read' ? 'read' : pose.kind}
    >
      {readings.map((reading) => (
        <div className="eqtile" data-pose={reading.key} key={reading.key}>
          <dt className="eqtile__label">{poseLabel(reading, robots)}</dt>
          <dd className="eqtile__value">{staleValue(poseValue(reading), old)}</dd>
        </div>
      ))}
      <div className="eqtile" data-metric={grip.key}>
        <dt className="eqtile__label">{grip.label}</dt>
        <dd className="eqtile__value">{grip.value}</dd>
      </div>
    </dl>
  )
}
